import supabase from '../supabase/supabase';

const GUEST_USAGE_KEY = 'meal_calculator_guest_usage';

class UsageTracker {
  constructor() {
    this.maxFreeCalculations = 5;
  }

  async getCurrentUserUsage() {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        return this._getGuestUserRecord();
      }

      let { data: usage, error } = await supabase
        .from('user_usage')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code === 'PGRST116') {
        // No record found, create one
        const { data: newUsage, error: insertError } = await supabase
          .from('user_usage')
          .insert({ user_id: user.id })
          .select()
          .single();

        if (insertError) {
          console.error('Error creating usage record:', insertError);
          return this._getGuestUserRecord(); // Fallback to guest record on error
        }
        usage = newUsage;
      } else if (error) {
        console.error('Error fetching usage data:', error);
        return this._getGuestUserRecord(); // Fallback
      }

      return usage;
    } catch (e) {
      console.error("Unexpected error in getCurrentUserUsage:", e);
      return this._getGuestUserRecord();
    }
  }

  _getGuestUserRecord() {
    try {
      const storedUsage = localStorage.getItem(GUEST_USAGE_KEY);
      if (storedUsage) {
        return JSON.parse(storedUsage);
      }
    } catch (error) {
      console.error("Error reading guest usage from localStorage:", error);
    }
    // Return default if nothing is stored or parsing fails
    return {
      user_id: null,
      successful_calculations: 0,
      total_calculations: 0,
      has_paid: false,
    };
  }

  _updateGuestUserRecord(updatedRecord) {
    try {
      localStorage.setItem(GUEST_USAGE_KEY, JSON.stringify(updatedRecord));
    } catch (error) {
      console.error("Error saving guest usage to localStorage:", error);
    }
  }

  _clearGuestUserRecord() {
    try {
      localStorage.removeItem(GUEST_USAGE_KEY);
    } catch (error) {
      console.error("Error clearing guest usage from localStorage:", error);
    }
  }

  async carryOverGuestUsage(userId) {
    const guestUsage = this._getGuestUserRecord();
    if (!userId || guestUsage.successful_calculations === 0) {
      this._clearGuestUserRecord(); // Clear it anyway on signup
      return;
    }

    console.log(`Carrying over ${guestUsage.successful_calculations} guest calculations to user ${userId}`);

    const { error } = await supabase
      .from('user_usage')
      .upsert({
        user_id: userId,
        successful_calculations: guestUsage.successful_calculations,
        total_calculations: guestUsage.total_calculations,
      }, { onConflict: 'user_id' });

    if (error) {
      console.error('Error carrying over guest usage:', error);
      // Don't clear local storage if the DB operation failed
      return;
    }

    this._clearGuestUserRecord();
    console.log('Guest usage carried over and cleared from local storage.');
  }

  handleLogout() {
    // Simply clear the guest record from local storage.
    // This ensures that the next time a guest user session starts, it's completely fresh.
    // This prevents the "stuck loading" state by removing any potentially corrupted or 
    // outdated guest data from the previous session.
    this._clearGuestUserRecord();
    console.log('Guest usage record cleared on logout to ensure a fresh session.');
  }

  async recordCalculation(isSuccessful) {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      // Guest user logic
      const guestUsage = this._getGuestUserRecord();
      if (!guestUsage.first_use) {
        guestUsage.first_use = Date.now();
      }
      guestUsage.last_use = Date.now();
      guestUsage.total_calculations += 1;
      if (isSuccessful) {
        guestUsage.successful_calculations += 1;
      }
      this._updateGuestUserRecord(guestUsage);
      return guestUsage;
    }

    // Logged-in user logic
    const { error } = await supabase.rpc('increment_calculation_count', {
      user_id_in: user.id,
      is_successful: isSuccessful
    });

    if (error) {
      console.error('Error recording calculation:', error);
    }

    return this.getCurrentUserUsage();
  }

  async needsPayment() {
    const usage = await this.getCurrentUserUsage();
    return usage.successful_calculations >= this.maxFreeCalculations && !usage.has_paid;
  }

  async recordPayment(subscriptionId = null) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return; // Can't record payment for a guest

    const { error } = await supabase
      .from('user_usage')
      .update({
        has_paid: true,
        payment_date: new Date().toISOString(),
        subscription_id: subscriptionId,
      })
      .eq('user_id', user.id);

    if (error) {
      console.error('Error recording payment:', error);
    }
  }

  async getRemainingFreeCalculations(usage = null) {
    const usageData = usage || await this.getCurrentUserUsage();
    if (usageData.has_paid) return Infinity;
    return Math.max(0, this.maxFreeCalculations - usageData.successful_calculations);
  }

  isCalculationSuccessful(mealPlan, targets, thresholds) {
    if (!mealPlan || !targets || !mealPlan.foods || mealPlan.foods.length === 0) return false;

    const { totals } = mealPlan;
    const targetCals = parseFloat(targets.calories);
    if (!targetCals) return false;

    const calorieError = Math.abs(totals.calories - targetCals) / targetCals;
    const withinCalorieThreshold = calorieError <= (thresholds.calories / 100);

    if (!thresholds.advancedMode) {
      return withinCalorieThreshold;
    }

    const targetProtein = parseFloat(targets.protein) || 0;
    const targetCarbs = parseFloat(targets.carbs) || 0;
    const targetFat = parseFloat(targets.fat) || 0;

    const proteinOk = targetProtein === 0 || Math.abs(totals.protein - targetProtein) / targetProtein <= (thresholds.protein / 100);
    const carbsOk = targetCarbs === 0 || Math.abs(totals.carbs - targetCarbs) / targetCarbs <= (thresholds.carbs / 100);
    const fatOk = targetFat === 0 || Math.abs(totals.fat - targetFat) / targetFat <= (thresholds.fat / 100);

    return withinCalorieThreshold && proteinOk && carbsOk && fatOk;
  }
}

export default new UsageTracker();